import { useEffect, useMemo, useState } from 'react';
import {
  getStaffName,
  getStaffTitle,
  parseAssignedGradeSplitIds,
  parseAssignedGrades,
} from '@car-pool/shared/schoolRecords';
import SchoolAvatar from './SchoolAvatar';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function getGradeSearchRank(grade, query) {
  if (!query) return 0;

  const label = normalize(grade?.label);
  if (label.startsWith(query)) return -10;
  if (label.includes(query)) return -5;
  return 0;
}

export default function StaffGradeAssignModal({
  staff,
  gradeNames = [],
  gradeSplits = [],
  groupingMode = 'grades',
  onClose,
  onAssign,
}) {
  const [search, setSearch] = useState('');
  const [candidateGradeCode, setCandidateGradeCode] = useState('');
  const [candidateGradeSplitId, setCandidateGradeSplitId] = useState('');
  const [selectedGradeCodes, setSelectedGradeCodes] = useState([]);
  const [selectedGradeSplitIds, setSelectedGradeSplitIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredGrades = useMemo(() => {
    const query = normalize(search);
    return [...gradeNames]
      .filter((grade) => !query || normalize(grade.label).includes(query))
      .sort((left, right) => {
        const rankDelta = getGradeSearchRank(left, query) - getGradeSearchRank(right, query);
        if (rankDelta !== 0) return rankDelta;
        return Number(left.grade_code) - Number(right.grade_code);
      });
  }, [gradeNames, search]);
  const showGradeSplits = groupingMode === 'splits_only' || groupingMode === 'grades_and_splits';
  const availableGradeSplits = useMemo(() => [...gradeSplits]
    .filter((split) => split.is_active === true || Number(split.is_active) === 1)
    .sort((left, right) => (
      Number(left.grade) - Number(right.grade)
      || String(left.split_code || '').localeCompare(String(right.split_code || ''))
    )), [gradeSplits]);

  useEffect(() => {
    if (!filteredGrades.length) {
      setCandidateGradeCode('');
      return;
    }

    if (!filteredGrades.some((grade) => String(grade.grade_code) === String(candidateGradeCode))) {
      setCandidateGradeCode(String(filteredGrades[0].grade_code));
    }
  }, [candidateGradeCode, filteredGrades]);

  useEffect(() => {
    if (!showGradeSplits || !availableGradeSplits.length) {
      setCandidateGradeSplitId('');
      return;
    }

    if (!availableGradeSplits.some((split) => String(split.id) === String(candidateGradeSplitId))) {
      setCandidateGradeSplitId(String(availableGradeSplits[0].id));
    }
  }, [availableGradeSplits, candidateGradeSplitId, showGradeSplits]);

  useEffect(() => {
    setSearch('');
    setCandidateGradeCode('');
    setCandidateGradeSplitId('');
    setSelectedGradeCodes(parseAssignedGrades(staff?.assigned_grades).map(String));
    setSelectedGradeSplitIds(parseAssignedGradeSplitIds(staff?.assigned_grade_split_ids).map(String));
    setSaving(false);
    setError('');
  }, [staff?.assigned_grade_split_ids, staff?.assigned_grades, staff?.id]);

  const selectedGrades = useMemo(
    () => selectedGradeCodes
      .map((gradeCode) => gradeNames.find((grade) => String(grade.grade_code) === String(gradeCode)))
      .filter(Boolean),
    [gradeNames, selectedGradeCodes]
  );
  const selectedGradeSplits = useMemo(
    () => selectedGradeSplitIds
      .map((splitId) => availableGradeSplits.find((split) => String(split.id) === String(splitId)))
      .filter(Boolean),
    [availableGradeSplits, selectedGradeSplitIds]
  );

  if (!staff) return null;

  const bestMatch = filteredGrades[0] || null;

  const addSelectedGrade = () => {
    if (!candidateGradeCode) return;
    setSelectedGradeCodes((current) => (
      current.includes(candidateGradeCode) ? current : [...current, candidateGradeCode]
    ));
  };

  const removeSelectedGrade = (gradeCode) => {
    setSelectedGradeCodes((current) => current.filter((item) => item !== gradeCode));
  };

  const addSelectedGradeSplit = () => {
    if (!candidateGradeSplitId) return;
    setSelectedGradeSplitIds((current) => (
      current.includes(candidateGradeSplitId) ? current : [...current, candidateGradeSplitId]
    ));
  };

  const removeSelectedGradeSplit = (splitId) => {
    setSelectedGradeSplitIds((current) => current.filter((item) => item !== splitId));
  };

  const handleAssign = async () => {
    if (!selectedGradeCodes.length && !selectedGradeSplitIds.length) return;

    setSaving(true);
    setError('');
    try {
      await onAssign?.(selectedGradeCodes.map(Number), selectedGradeSplitIds.map(Number));
    } catch (err) {
      setError(err.message || 'Failed to assign grade coverage');
      setSaving(false);
    }
  };

  return (
    <div className="sd-support-overlay" onClick={onClose}>
      <div className="sd-student-parent-modal" onClick={(event) => event.stopPropagation()}>
        <div className="sd-student-parent-modal-header">
          <button type="button" className="sd-support-back" onClick={onClose} aria-label="Back">
            ←
          </button>

          <div className="sd-student-parent-modal-copy">
            <h2>Assign Teacher Group Coverage</h2>
            <p>Select one or more grades or class splits this teacher is responsible for.</p>
          </div>
        </div>

        <div className="sd-student-parent-student-card">
          <SchoolAvatar record={staff} name={getStaffName(staff)} className="sd-student-parent-avatar" />
          <div className="sd-student-parent-student-copy">
            <div className="sd-student-parent-student-name">{getStaffName(staff)}</div>
            <div className="sd-student-parent-student-meta">{getStaffTitle(staff)}</div>
          </div>
        </div>

        <div className="sd-student-parent-form-card">
          <label className="sd-student-parent-label" htmlFor="staff-grade-search">Search all grades</label>
          <input
            id="staff-grade-search"
            className="sd-student-parent-input"
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by grade label"
          />

          {bestMatch ? (
            <div className="sd-student-parent-best-match">
              Suggested grade: <strong>{bestMatch.label}</strong>
            </div>
          ) : (
            <div className="sd-student-parent-best-match">No grades found.</div>
          )}

          <div className="sd-student-parent-select-row">
            <select
              className="sd-student-parent-select"
              value={candidateGradeCode}
              onChange={(event) => setCandidateGradeCode(event.target.value)}
              disabled={!filteredGrades.length}
            >
              {filteredGrades.length ? filteredGrades.map((grade) => (
                <option key={grade.grade_code} value={grade.grade_code}>
                  {grade.label}
                </option>
              )) : (
                <option value="">No grades available</option>
              )}
            </select>

            <button
              type="button"
              className="sd-student-parent-add"
              onClick={addSelectedGrade}
              disabled={!candidateGradeCode}
            >
              Add Grade
            </button>
          </div>

          <div className="sd-student-parent-selected">
            {selectedGrades.length ? selectedGrades.map((grade) => (
              <div key={grade.grade_code} className="sd-student-parent-pill">
                <span>{grade.label}</span>
                <button type="button" onClick={() => removeSelectedGrade(String(grade.grade_code))} aria-label={`Remove ${grade.label}`}>
                  ×
                </button>
              </div>
            )) : (
              <div className="sd-student-parent-empty">No grade coverage selected yet.</div>
            )}
          </div>

          {showGradeSplits && availableGradeSplits.length ? (
            <>
              <label className="sd-student-parent-label" htmlFor="staff-grade-split-select">
                Class splits
              </label>
              <div className="sd-student-parent-select-row">
                <select
                  id="staff-grade-split-select"
                  className="sd-student-parent-select"
                  value={candidateGradeSplitId}
                  onChange={(event) => setCandidateGradeSplitId(event.target.value)}
                >
                  {availableGradeSplits.map((split) => {
                    const grade = gradeNames.find((item) => Number(item.grade_code) === Number(split.grade));
                    const label = groupingMode === 'splits_only'
                      ? split.split_code
                      : `${grade?.label || `Grade ${split.grade}`} - ${split.split_code}`;
                    return <option key={split.id} value={split.id}>{label}</option>;
                  })}
                </select>

                <button type="button" className="sd-student-parent-add" onClick={addSelectedGradeSplit}>
                  Add Split
                </button>
              </div>

              <div className="sd-student-parent-selected">
                {selectedGradeSplits.length ? selectedGradeSplits.map((split) => (
                  <div key={split.id} className="sd-student-parent-pill">
                    <span>{split.split_code}</span>
                    <button
                      type="button"
                      onClick={() => removeSelectedGradeSplit(String(split.id))}
                      aria-label={`Remove ${split.split_code}`}
                    >
                      ×
                    </button>
                  </div>
                )) : (
                  <div className="sd-student-parent-empty">No split coverage selected yet.</div>
                )}
              </div>
            </>
          ) : null}

          {error ? <div className="sa-error">{error}</div> : null}

          <button
            type="button"
            className="sd-support-send"
            onClick={handleAssign}
            disabled={saving || (!selectedGradeCodes.length && !selectedGradeSplitIds.length)}
          >
            {saving ? 'Saving…' : 'Assign Groups'}
          </button>
        </div>
      </div>
    </div>
  );
}
